import { z } from "zod";

export const roles = ["resident_owner", "resident_tenant", "resident_occupant", "property_admin", "super_admin", "maintenance", "security_desk", "technical_admin"] as const;
export const roleFamilies = ["resident", "employee"] as const;
export const accountStatuses = ["pending", "active", "suspended", "rejected"] as const;
export const ticketStatuses = ["open", "triaged", "scheduled", "in_progress", "awaiting_parts", "awaiting_resident", "awaiting_vendor", "resolved", "reopened", "closed", "cancelled"] as const;
export const ticketPriorities = ["emergency", "urgent", "routine", "planned"] as const;
export const ticketRequestTypes = ["plumbing", "electrical", "air_conditioning", "elevator", "generator_pump", "fire_life_safety", "cleaning_pest_control", "common_area_damage", "security_access", "other"] as const;
export const ticketSources = ["resident", "guard", "pmo", "engineering", "vendor", "preventive"] as const;
export const ledgerEntryTypes = ["charge", "payment", "adjustment", "penalty"] as const;
export const ledgerCategories = ["association_dues", "utilities", "parking_fee", "other", "opening_balance"] as const;
export const documentStatuses = [
  "uploading",
  "pending_scan",
  "scanning_raw",
  "normalizing",
  "scanning_final",
  "clean",
  "rejected",
  "scan_delayed",
  "scan_failed",
] as const;

export type Role = (typeof roles)[number];
export type RoleFamily = (typeof roleFamilies)[number];
export type TicketStatus = (typeof ticketStatuses)[number];
export type TicketPriority = (typeof ticketPriorities)[number];
export type TicketRequestType = (typeof ticketRequestTypes)[number];
export type TicketSource = (typeof ticketSources)[number];
export const residentAndAdminRoles = ["resident_owner", "resident_tenant", "property_admin", "super_admin"] as const satisfies readonly Role[];

export const createTicketSchema = z.object({
  unitId: z.string().min(1).optional(),
  buildingId: z.string().min(1).optional(),
  categoryId: z.string().min(1),
  locationType: z.enum(["unit", "common_area"]),
  locationLabel: z.string().trim().min(2).max(120),
  title: z.string().trim().min(4).max(120),
  description: z.string().trim().min(10).max(3_000),
  requestType: z.enum(ticketRequestTypes).default("other"),
  priority: z.enum(ticketPriorities).default("routine"),
  source: z.enum(ticketSources).default("resident"),
  permissionToEnter: z.boolean().default(false),
  preferredAccessStart: z.number().int().positive().optional(),
  preferredAccessEnd: z.number().int().positive().optional(),
  emergencyCallback: z.string().trim().max(40).optional(),
  equipmentContext: z.string().trim().max(240).optional(),
  attachmentDocumentIds: z.array(z.string().min(1)).max(5).default([]),
}).superRefine((value, context) => {
  if (value.locationType === "common_area" && !value.buildingId) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["buildingId"], message: "Common-area tickets require a building" });
  }
  if (value.locationType === "common_area" && value.unitId) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["unitId"], message: "Common-area tickets cannot include a unit" });
  }
  if (value.preferredAccessStart && value.preferredAccessEnd && value.preferredAccessEnd <= value.preferredAccessStart) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["preferredAccessEnd"], message: "Preferred access end must be after the start" });
  }
  if ((value.preferredAccessStart === undefined) !== (value.preferredAccessEnd === undefined)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["preferredAccessEnd"], message: "Preferred access requires both a start and end" });
  }
});

export const ticketTransitionSchema = z.object({
  nextStatus: z.enum(ticketStatuses),
  comment: z.string().trim().max(1_000).optional(),
  expectedVersion: z.number().int().positive().optional(),
  evidenceDocumentIds: z.array(z.string().min(1)).max(5).default([]),
});

export const ticketTriageSchema = z.object({
  priority: z.enum(ticketPriorities),
  requestType: z.enum(ticketRequestTypes),
  nextAction: z.string().trim().min(2).max(500),
  comment: z.string().trim().max(1_000).optional(),
  expectedVersion: z.number().int().positive().optional(),
});

export const maintenanceAppointmentSchema = z.object({
  assignmentId: z.string().min(1),
  appointmentId: z.string().min(1).optional(),
  startsAt: z.number().int().positive(),
  endsAt: z.number().int().positive(),
  note: z.string().trim().max(500).optional(),
  expectedVersion: z.number().int().positive().optional(),
}).refine((value) => value.endsAt > value.startsAt, {
  path: ["endsAt"],
  message: "Appointment end must be after the start",
});

export const maintenanceFeedbackSchema = z.object({
  resolutionEventId: z.string().min(1),
  score: z.number().int().min(1).max(5),
  comment: z.string().trim().max(1_000).optional(),
  expectedVersion: z.number().int().positive().optional(),
});

export const maintenanceEstimateSchema = z.object({
  vendorId: z.string().min(1).optional(),
  amountCentavos: z.number().int().nonnegative(),
  description: z.string().trim().min(3).max(1_000),
  quoteDocumentId: z.string().min(1).optional(),
  changeOrderOfId: z.string().min(1).optional(),
  expectedVersion: z.number().int().positive().optional(),
});

export const maintenanceCostSchema = z.object({
  estimateId: z.string().min(1).optional(),
  amountCentavos: z.number().int().positive(),
  costType: z.enum(["approved", "incurred", "chargeback"]),
  description: z.string().trim().min(3).max(1_000),
  responsibility: z.enum(["association", "resident", "vendor_warranty", "unallocated"]),
  expectedVersion: z.number().int().positive().optional(),
  allocations: z.array(z.object({
    scopeType: z.enum(["unit", "building", "fund", "unallocated"]),
    unitId: z.string().min(1).optional(),
    buildingId: z.string().min(1).optional(),
    fundCode: z.string().trim().max(80).optional(),
    amountCentavos: z.number().int().positive(),
  })).max(50).default([]),
}).superRefine((value, context) => {
  for (const [index, allocation] of value.allocations.entries()) {
    const fields = [allocation.unitId, allocation.buildingId, allocation.fundCode].filter(Boolean).length;
    const valid = allocation.scopeType === "unit" ? Boolean(allocation.unitId) && fields === 1
      : allocation.scopeType === "building" ? Boolean(allocation.buildingId) && fields === 1
        : allocation.scopeType === "fund" ? Boolean(allocation.fundCode) && fields === 1
          : fields === 0;
    if (!valid) context.addIssue({ code: z.ZodIssueCode.custom, path: ["allocations", index], message: "Allocation scope and reference must match" });
  }
});

export const maintenanceIncidentSchema = z.object({
  incidentType: z.enum(["life_safety", "flooding", "fire", "electrical_hazard", "elevator_entrapment", "security", "other"]),
  severity: z.enum(["critical", "high"]),
  ownerUserId: z.string().min(1).optional(),
  safetyMessage: z.string().trim().min(3).max(1_000),
});

export const maintenanceVendorProfileSchema = z.object({
  vendorId: z.string().min(1),
  serviceScope: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
  qualificationStatus: z.enum(["pending", "approved", "suspended"]).default("pending"),
  qualificationEvidenceDocumentIds: z.array(z.string().min(1)).max(20).default([]),
  insuranceDocumentId: z.string().min(1).optional(),
  licenseDocumentId: z.string().min(1).optional(),
  insuranceExpiresAt: z.number().int().positive().optional(),
  licenseExpiresAt: z.number().int().positive().optional(),
  warrantyDays: z.number().int().nonnegative().max(3_650).default(0),
  rateCardJson: z.string().min(2).max(10_000).default("{}"),
  active: z.boolean().default(false),
});

export const maintenanceVendorTechnicianSchema = z.object({
  displayName: z.string().trim().min(2).max(120),
  phone: z.string().trim().max(40).optional(),
  qualification: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
  qualificationEvidenceDocumentIds: z.array(z.string().min(1)).max(20).default([]),
  active: z.boolean().default(true),
});

export const maintenanceSlaPolicySchema = z.object({
  categoryId: z.string().min(1),
  priority: z.enum(ticketPriorities),
  version: z.number().int().positive().optional(),
  effectiveFrom: z.string().date(),
  calendarId: z.string().min(1),
  acknowledgementMinutes: z.number().int().positive(),
  dispatchMinutes: z.number().int().positive(),
  resolutionMinutes: z.number().int().positive(),
  maxPauseMinutes: z.number().int().nonnegative().default(0),
  escalationIntervalMinutes: z.number().int().positive().default(60),
  riskWindowMinutes: z.number().int().positive().default(240),
  reopenWindowMinutes: z.number().int().nonnegative().default(10080),
  approvalThresholdCentavos: z.number().int().nonnegative().default(0),
  pauseStatuses: z.array(z.enum(["awaiting_parts", "awaiting_resident", "awaiting_vendor"])).default(["awaiting_parts", "awaiting_resident", "awaiting_vendor"]),
  escalationRecipients: z.array(z.string().min(1)).max(50).default([]),
  clockStartRules: z.object({
    acknowledgement: z.enum(["submission", "triage"]).default("submission"),
    dispatch: z.enum(["submission", "triage"]).default("triage"),
    resolution: z.enum(["submission", "triage"]).default("triage"),
  }).default({ acknowledgement: "submission", dispatch: "triage", resolution: "triage" }),
  resolutionDefinition: z.object({ completionNoteRequired: z.boolean().default(true), evidenceRequired: z.boolean().default(false) }).default({ completionNoteRequired: true, evidenceRequired: false }),
  resolutionRequiresConfirmation: z.boolean().default(true),
});

export const ticketVisitScheduleSchema = z.object({
  workerUserId: z.string().min(1),
  scheduledVisitStart: z.number().int().positive(),
  scheduledVisitEnd: z.number().int().positive(),
  note: z.string().trim().max(500).optional(),
  expectedVersion: z.number().int().positive().optional(),
}).refine((value) => value.scheduledVisitEnd > value.scheduledVisitStart, {
  path: ["scheduledVisitEnd"],
  message: "Visit end must be after the start",
});

export const parkingPatrolObservationSchema = z.object({
  parkingSlotId: z.string().min(1).optional(),
  locationLabel: z.string().trim().min(2).max(160),
  observedAt: z.number().int().positive(),
  observationType: z.enum(["vehicle", "safety", "damage", "obstruction", "lighting", "other"]),
  vehicleType: z.string().trim().max(80).optional(),
  vehicleMakeModel: z.string().trim().max(120).optional(),
  vehicleColor: z.string().trim().max(80).optional(),
  plateNumber: z.string().trim().max(40).optional(),
  note: z.string().trim().min(2).max(1_000),
});

export const createLedgerEntrySchema = z.object({
  mutationId: z.string().uuid(),
  type: z.enum(ledgerEntryTypes),
  category: z.enum(ledgerCategories),
  amountCentavos: z.number().int().safe(),
  description: z.string().trim().min(2).max(500),
  correctsEntryId: z.string().min(1).optional(),
  effectiveDate: z.string().date(),
});

export const createAnnouncementSchema = z.object({
  title: z.string().trim().min(3).max(140),
  body: z.string().trim().min(10).max(5_000),
  buildingIds: z.array(z.string().min(1)).min(1),
  audienceRelationship: z.enum(["all", "owner", "tenant"]).default("all"),
  priority: z.enum(["normal", "important", "emergency"]).default("normal"),
  expiresAt: z.number().int().positive().optional(),
  media: z.array(z.object({ documentId: z.string().min(1), altText: z.string().trim().min(3).max(300) })).max(6).default([]),
  publishNow: z.boolean().default(false),
});

export const communityPostCategories = ["general", "recommendations", "lost_found", "events", "help_requests"] as const;
export const createCommunityPostSchema = z.object({
  category: z.enum(communityPostCategories),
  title: z.string().trim().min(4).max(140),
  body: z.string().trim().min(10).max(5_000),
  media: z.array(z.object({ documentId: z.string().min(1), altText: z.string().trim().min(3).max(300) })).max(5).default([]),
});

export const createCommunityCommentSchema = z.object({ body: z.string().trim().min(1).max(2_000) });
export const communityGuidelineCategories = ["commercial_solicitation", "harassment", "personal_information", "spam", "misleading_information", "safety_security", "inappropriate_media", "other"] as const;
export const communityRemovalSchema = z.object({ guidelineCategory: z.enum(communityGuidelineCategories), reason: z.string().trim().min(5).max(500), restrictionDays: z.number().int().min(0).max(365).default(0) });

const marketplaceSubmissionBaseSchema = z.object({
  publisherType: z.enum(["resident_individual", "resident_business", "external_individual", "external_business"]),
  submitterName: z.string().trim().min(2).max(120), submitterEmail: z.email(), contactPhone: z.string().trim().max(60).optional(), legalName: z.string().trim().max(160).optional(),
  listingType: z.enum(["product", "service"]), category: z.string().trim().min(2).max(80), title: z.string().trim().min(4).max(140), body: z.string().trim().min(10).max(5_000),
  priceCentavos: z.number().int().min(0).max(1_000_000_000).optional(), contactPreference: z.enum(["email", "phone", "url"]), contactValue: z.string().trim().min(3).max(500),
  buildingIds: z.array(z.string().min(1)).min(1), requestedAudienceRelationship: z.enum(["all", "owner", "tenant"]).default("all"), requestedExpiresAt: z.number().int().positive(),
  media: z.array(z.object({ documentId: z.string().min(1), altText: z.string().trim().min(3).max(300) })).max(6).default([]), consent: z.literal(true),
});
export const marketplaceSubmissionSchema = marketplaceSubmissionBaseSchema.superRefine((submission, context) => {
  if (submission.publisherType.endsWith("business") && !submission.legalName) context.addIssue({ code: "custom", path: ["legalName"], message: "Business name is required" });
  if (submission.contactPreference === "email" && !z.email().safeParse(submission.contactValue).success) context.addIssue({ code: "custom", path: ["contactValue"], message: "Valid contact email is required" });
  if (submission.contactPreference === "url" && !z.url().safeParse(submission.contactValue).success) context.addIssue({ code: "custom", path: ["contactValue"], message: "Valid contact URL is required" });
});
export const externalMarketplaceSubmissionSchema = marketplaceSubmissionBaseSchema.omit({ media: true }).extend({ publisherType: z.enum(["external_individual", "external_business"]), turnstileToken: z.string().min(1) }).superRefine((submission, context) => {
  if (submission.publisherType.endsWith("business") && !submission.legalName) context.addIssue({ code: "custom", path: ["legalName"], message: "Business name is required" });
  if (submission.contactPreference === "email" && !z.email().safeParse(submission.contactValue).success) context.addIssue({ code: "custom", path: ["contactValue"], message: "Valid contact email is required" });
  if (submission.contactPreference === "url" && !z.url().safeParse(submission.contactValue).success) context.addIssue({ code: "custom", path: ["contactValue"], message: "Valid contact URL is required" });
});
export const marketplaceSubmissionDecisionSchema = z.object({ decision: z.enum(["approved", "changes_requested", "rejected"]), reason: z.string().trim().min(5).max(1_000), reviewNotes: z.string().trim().min(5).max(1_000).optional() });

export const eventCategories = ["movie", "sports", "community", "meeting", "workshop", "other"] as const;
export const eventRegistrationModes = ["individual", "group", "team"] as const;
export const createEventSchema = z.object({
  title: z.string().trim().min(4).max(160),
  description: z.string().trim().min(20).max(10_000),
  category: z.enum(eventCategories),
  visibility: z.enum(["residents_only", "public"]),
  audienceRelationship: z.enum(["all", "owner", "tenant"]).default("all"),
  registrationMode: z.enum(eventRegistrationModes),
  venueName: z.string().trim().min(2).max(160),
  facilityId: z.string().min(1).optional(),
  startsAt: z.number().int().positive(),
  endsAt: z.number().int().positive(),
  registrationOpensAt: z.number().int().positive(),
  registrationClosesAt: z.number().int().positive(),
  capacityAttendees: z.number().int().min(1).max(10_000).optional(),
  maxPartySize: z.number().int().min(1).max(50),
  waitlistEnabled: z.boolean().default(true),
  externalRegistrationEnabled: z.boolean().default(false),
  buildingIds: z.array(z.string().min(1)).min(1),
  media: z.array(z.object({ documentId: z.string().min(1), altText: z.string().trim().min(3).max(300) })).max(3).default([]),
  publishNow: z.boolean().default(false),
}).superRefine((event, context) => {
  if (event.endsAt <= event.startsAt) context.addIssue({ code: "custom", path: ["endsAt"], message: "Event must end after it starts" });
  if (event.registrationClosesAt > event.startsAt || event.registrationClosesAt <= event.registrationOpensAt) context.addIssue({ code: "custom", path: ["registrationClosesAt"], message: "Invalid registration window" });
  if (event.visibility === "residents_only" && event.externalRegistrationEnabled === false) return;
});

export const eventRegistrationSchema = z.object({
  attendeeNames: z.array(z.string().trim().min(2).max(120)).min(1).max(50),
  teamName: z.string().trim().min(2).max(120).optional(),
});

export const externalEventRegistrationSchema = eventRegistrationSchema.extend({
  name: z.string().trim().min(2).max(120),
  email: z.email(),
  claimedBuildingId: z.string().min(1).optional(),
  claimedUnitCode: z.string().trim().min(1).max(30).optional(),
  turnstileToken: z.string().min(1),
  consent: z.literal(true),
});

export const visitorPurposes = ["social_visit", "family_visit", "service_provider", "delivery", "other"] as const;
export const createVisitorRegistrationSchema = z.object({
  unitId: z.string().min(1).max(100),
  guestName: z.string().trim().min(2).max(120),
  purpose: z.enum(visitorPurposes),
  partySize: z.number().int().min(1).max(20),
  visitStartsAt: z.number().int().positive(),
  visitEndsAt: z.number().int().positive(),
  vehiclePlate: z.string().trim().max(20).optional(),
  notes: z.string().trim().max(500).optional(),
}).superRefine((visit, context) => {
  if (visit.visitEndsAt <= visit.visitStartsAt) context.addIssue({ code: "custom", path: ["visitEndsAt"], message: "Visit must end after it starts" });
  if (visit.visitEndsAt - visit.visitStartsAt > 86_400) context.addIssue({ code: "custom", path: ["visitEndsAt"], message: "A visitor pass cannot exceed 24 hours" });
});

export const visitorPassTokenSchema = z.object({ token: z.string().min(40).max(64) });
export const visitorTransitionSchema = z.object({
  token: z.string().min(40).max(64).optional(),
  visitorId: z.string().min(1).max(100).optional(),
  action: z.enum(["check_in", "check_out", "deny"]),
  gateLocation: z.string().trim().min(2).max(120).default("Main gate"),
  note: z.string().trim().max(500).optional(),
}).superRefine((transition, context) => {
  if (!transition.token && !transition.visitorId) context.addIssue({ code: "custom", path: ["token"], message: "A visitor token or ID is required" });
  if (transition.action === "deny" && (!transition.note || transition.note.length < 3)) context.addIssue({ code: "custom", path: ["note"], message: "A denial reason is required" });
});

export const permitTypes = ["move_in_out", "work", "renovation"] as const;
export const createPermitSchema = z.object({
  unitId: z.string().min(1).max(100),
  permitType: z.enum(permitTypes),
  validFrom: z.number().int().positive(),
  validUntil: z.number().int().positive(),
  direction: z.enum(["move_in", "move_out"]).optional(),
  itemDescription: z.string().trim().max(2_000).optional(),
  elevatorUse: z.boolean().optional(),
  workerNames: z.array(z.string().trim().min(2).max(120)).max(50).optional(),
  approvedVendorId: z.string().min(1).max(100).optional(),
  contractorName: z.string().trim().max(160).optional(),
  workDescription: z.string().trim().max(4_000).optional(),
  equipmentList: z.string().trim().max(2_000).optional(),
  scopeOfWork: z.string().trim().max(5_000).optional(),
  notes: z.string().trim().max(1_000).optional(),
}).superRefine((permit, context) => {
  if (permit.validUntil <= permit.validFrom) context.addIssue({ code: "custom", path: ["validUntil"], message: "Permit must end after it starts" });
  if (permit.validUntil - permit.validFrom > 60 * 60 * 24 * 180) context.addIssue({ code: "custom", path: ["validUntil"], message: "Permit window cannot exceed 180 days" });
  if (permit.permitType === "move_in_out" && (!permit.direction || !permit.itemDescription || permit.itemDescription.length < 5)) context.addIssue({ code: "custom", path: ["itemDescription"], message: "Direction and item description are required" });
  if (permit.permitType === "work" && (!permit.workerNames?.length || !permit.contractorName || !permit.workDescription || permit.workDescription.length < 5)) context.addIssue({ code: "custom", path: ["workDescription"], message: "Workers, contractor, and work description are required" });
  if (permit.permitType === "renovation" && (!permit.contractorName || !permit.scopeOfWork || permit.scopeOfWork.length < 10)) context.addIssue({ code: "custom", path: ["scopeOfWork"], message: "Contractor and renovation scope are required" });
});

export const permitDecisionSchema = z.object({
  decision: z.enum(["approve", "request_changes", "reject"]),
  comments: z.string().trim().max(1_000).optional(),
}).superRefine((decision, context) => {
  if (decision.decision !== "approve" && (!decision.comments || decision.comments.length < 3)) context.addIssue({ code: "custom", path: ["comments"], message: "Comments are required" });
});

export const permitWorkflowSchema = z.object({
  permitType: z.enum(permitTypes),
  stages: z.array(z.object({ label: z.string().trim().min(2).max(120), approverRole: z.enum(["property_admin", "super_admin", "maintenance"]) })).min(1).max(8),
});

export const createPackageDeliverySchema = z.object({
  unitId: z.string().min(1).max(100),
  recipientName: z.string().trim().min(2).max(120).optional(),
  courierName: z.string().trim().min(2).max(120),
  trackingReference: z.string().trim().max(120).optional(),
  description: z.string().trim().min(3).max(1_000),
  photoDocumentId: z.string().min(1).max(100).optional(),
  notes: z.string().trim().max(500).optional(),
});

export const packageDeliveryTransitionSchema = z.object({
  action: z.enum(["collect", "return"]),
  collectedByName: z.string().trim().max(120).optional(),
  note: z.string().trim().max(500).optional(),
}).superRefine((transition, context) => {
  if (transition.action === "collect" && (!transition.collectedByName || transition.collectedByName.length < 2)) context.addIssue({ code: "custom", path: ["collectedByName"], message: "Collector name is required" });
  if (transition.action === "return" && (!transition.note || transition.note.length < 3)) context.addIssue({ code: "custom", path: ["note"], message: "Return reason is required" });
});

export const createVehicleSchema = z.object({
  unitId: z.string().min(1).max(100), plateNumber: z.string().trim().min(3).max(20).transform((value) => value.toUpperCase()),
  make: z.string().trim().min(2).max(80), model: z.string().trim().min(1).max(80), color: z.string().trim().min(2).max(50),
});
export const createParkingListingSchema = z.object({
  parkingSlotId: z.string().min(1).max(100), monthlyRateCentavos: z.number().int().min(0).max(100_000_000), notes: z.string().trim().max(500).optional(),
});
export const parkingRentalRequestSchema = z.object({
  listingId: z.string().min(1).max(100), vehicleId: z.string().min(1).max(100), requestedStartDate: z.string().date(), requestedEndDate: z.string().date().optional(), note: z.string().trim().max(500).optional(),
}).superRefine((request, context) => { if (request.requestedEndDate && request.requestedEndDate < request.requestedStartDate) context.addIssue({ code: "custom", path: ["requestedEndDate"], message: "End date must follow start date" }); });
export const parkingRentalDecisionSchema = z.object({ decision: z.enum(["approve", "reject"]), note: z.string().trim().max(500).optional() }).superRefine((decision, context) => { if (decision.decision === "reject" && (!decision.note || decision.note.length < 3)) context.addIssue({ code: "custom", path: ["note"], message: "Rejection reason is required" }); });
export const createRfidAssignmentSchema = z.object({ cardIdentifier: z.string().trim().min(4).max(80), residentUserId: z.string().min(1).max(100), unitId: z.string().min(1).max(100) });
export const rfidStatusSchema = z.object({ action: z.enum(["report_lost", "deactivate"]), reason: z.string().trim().min(3).max(500) });

export const employeeProfileSchema = z.object({
  userId: z.string().min(1).max(100), employeeNumber: z.string().trim().min(2).max(30),
  employeeType: z.enum(["security", "engineering", "housekeeping", "admin_office"]), jobTitle: z.string().trim().min(2).max(100),
  phone: z.string().trim().max(40).optional(), hireDate: z.string().date().optional(),
});
export const staffShiftSchema = z.object({
  title: z.string().trim().min(3).max(120), location: z.string().trim().min(2).max(160),
  startsAt: z.string().datetime(), endsAt: z.string().datetime(), requiredHeadcount: z.number().int().min(1).max(50),
  employeeProfileIds: z.array(z.string().min(1)).max(50).default([]), notes: z.string().trim().max(500).optional(),
}).superRefine((shift, context) => { if (shift.endsAt <= shift.startsAt) context.addIssue({ code: "custom", path: ["endsAt"], message: "End must follow start" }); });
export const staffLeaveSchema = z.object({ leaveType: z.enum(["vacation", "sick", "emergency", "other"]), startsOn: z.string().date(), endsOn: z.string().date(), reason: z.string().trim().min(3).max(500) }).superRefine((leave, context) => { if (leave.endsOn < leave.startsOn) context.addIssue({ code: "custom", path: ["endsOn"], message: "End date must follow start date" }); });
export const staffLeaveDecisionSchema = z.object({ decision: z.enum(["approve", "reject"]), note: z.string().trim().max(500).optional() }).superRefine((decision, context) => { if (decision.decision === "reject" && (!decision.note || decision.note.length < 3)) context.addIssue({ code: "custom", path: ["note"], message: "Rejection reason is required" }); });
export const attendanceEventSchema = z.object({ shiftId: z.string().min(1).max(100).optional(), eventType: z.enum(["clock_in", "clock_out"]), note: z.string().trim().max(500).optional() }).superRefine((event, context) => {
  if (!event.shiftId && (!event.note || event.note.length < 3)) context.addIssue({ code: "custom", path: ["note"], message: "A reason is required for unscheduled attendance" });
});
export const accessCredentialTypes = ["visitor", "permit", "reservation", "pool", "package"] as const;
export const accessCredentialStatuses = ["pending", "active", "used", "expired", "revoked", "denied", "superseded"] as const;
export const accessCredentialEntryModes = ["single_entry", "multi_entry", "entry_exit"] as const;
export const accessCredentialDirections = ["entry", "exit"] as const;
export const accessWalletVerifySchema = z.object({
  token: z.string().min(40).max(4_096),
  gateScope: z.string().trim().min(1).max(120),
  direction: z.enum(accessCredentialDirections).default("entry"),
});

export const guardActions = ["verify", "check_in", "deny", "checkout", "hold", "override"] as const;
export const guardCredentialTypes = [...accessCredentialTypes, "vehicle"] as const;
export const guardGateStatuses = ["draft", "active", "inactive"] as const;
export const guardEventStatuses = ["received", "applied", "duplicate", "conflict", "rejected"] as const;

export const guardGatePolicySchema = z.object({
  gateScope: z.string().trim().min(1).max(120),
  name: z.string().trim().min(2).max(120),
  createOnly: z.boolean().default(false),
  status: z.enum(guardGateStatuses).default("draft"),
  buildingIds: z.array(z.string().min(1).max(100)).max(100).default([]),
  allowedActions: z.array(z.enum(guardActions)).min(1).max(guardActions.length).default([...guardActions]),
  acceptedKeyIds: z.array(z.string().trim().min(1).max(200)).max(20).default([]),
  cacheTtlSeconds: z.number().int().min(300).max(7_200).default(1_800),
  maxClockSkewSeconds: z.number().int().min(0).max(120).default(120),
  requireLocalIdCheck: z.boolean().default(false),
  guestIntakeEnabled: z.boolean().default(true),
  guestIntakeTypes: z.array(z.enum(["visitor", "pool"])).min(1).max(2).default(["visitor", "pool"]),
  retentionDays: z.number().int().min(1).max(3_650).default(90),
}).superRefine((policy, context) => {
  if (policy.allowedActions.includes("override") && !policy.allowedActions.some((action) => ["check_in", "checkout", "deny"].includes(action))) {
    context.addIssue({ code: "custom", path: ["allowedActions"], message: "Override requires at least one overridable action" });
  }
});

export const guardDeviceRegistrationSchema = z.object({
  gateScope: z.string().trim().min(1).max(120),
  deviceLabel: z.string().trim().min(2).max(120),
});

const guardDevicePublicKeySchema = z.record(z.string(), z.unknown()).superRefine((value, context) => {
  if (value.kty !== "EC" || value.crv !== "P-256" || typeof value.x !== "string" || typeof value.y !== "string" || value.d !== undefined) {
    context.addIssue({ code: "custom", path: [], message: "A public P-256 EC key is required" });
  }
});

const guardDeviceEnrollmentSecretSchema = z.object({
  enrollmentToken: z.string().trim().min(40).max(120).optional(),
  setupCode: z.string().trim().regex(/^\d{6}$/u).optional(),
}).superRefine((value, context) => {
  if (Boolean(value.enrollmentToken) === Boolean(value.setupCode)) {
    context.addIssue({ code: "custom", path: [], message: "Exactly one enrollment token or setup code is required" });
  }
});

export const guardDeviceEnrollmentPreviewSchema = guardDeviceEnrollmentSecretSchema;

export const guardDeviceEnrollmentSchema = guardDeviceEnrollmentSecretSchema.extend({
  publicKeyJwk: guardDevicePublicKeySchema,
});

export const guardDevicePairSchema = z.object({
  deviceId: z.string().min(1).max(100),
  pairingCode: z.string().trim().min(8).max(120),
  publicKeyJwk: guardDevicePublicKeySchema,
});

export const guardDeviceShiftStartSchema = z.object({
  publicKeyJwk: guardDevicePublicKeySchema,
});

export const guardDeviceShiftEndSchema = z.object({
  sessionId: z.string().min(1).max(100),
});

export const guardEventSchema = z.object({
  eventId: z.uuid(),
  deviceSequence: z.number().int().positive(),
  sessionId: z.string().min(1).max(100).optional(),
  bundleId: z.string().uuid(),
  credentialId: z.string().min(1).max(160).optional(),
  credentialType: z.enum(guardCredentialTypes).optional(),
  sourceId: z.string().min(1).max(160).optional(),
  credentialVersion: z.number().int().positive().optional(),
  action: z.enum(guardActions),
  gateScope: z.string().trim().min(1).max(120),
  direction: z.enum(accessCredentialDirections).default("entry"),
  localWallTime: z.number().int().positive(),
  localMonotonicMs: z.number().int().nonnegative(),
  bestKnownServerTime: z.number().int().positive(),
  reason: z.string().trim().max(500).optional(),
  localIdChecked: z.boolean().default(false),
  overrideAction: z.enum(["check_in", "checkout", "deny"]).optional(),
  overrideGrantId: z.string().min(1).max(160).optional(),
  overrideGrantToken: z.string().min(40).max(4_096).optional(),
  nonceHash: z.string().regex(/^[a-f0-9]{64}$/u).optional(),
  payloadHash: z.string().regex(/^[a-f0-9]{64}$/u),
  signature: z.string().min(40).max(4_096),
}).superRefine((event, context) => {
  if (!event.credentialType) context.addIssue({ code: "custom", path: ["credentialType"], message: "A credential type is required" });
  if (!event.sourceId) context.addIssue({ code: "custom", path: ["sourceId"], message: "A source is required" });
  if (event.credentialType === "vehicle" && event.credentialId) context.addIssue({ code: "custom", path: ["credentialId"], message: "Vehicles use sourceId instead of credentialId" });
  if (event.credentialType === "vehicle" && event.action !== "verify") context.addIssue({ code: "custom", path: ["action"], message: "Vehicles support verification only" });
  if (event.credentialType && event.credentialType !== "vehicle" && !event.credentialId) context.addIssue({ code: "custom", path: ["credentialId"], message: "A projected credential is required" });
  if (["deny", "hold", "override"].includes(event.action) && (!event.reason || event.reason.length < 3)) context.addIssue({ code: "custom", path: ["reason"], message: "A reason of at least three characters is required" });
  if (event.action === "override" && !event.overrideGrantId) context.addIssue({ code: "custom", path: ["overrideGrantId"], message: "A supervisor grant is required" });
  if (event.action === "override" && !event.overrideAction) context.addIssue({ code: "custom", path: ["overrideAction"], message: "The overridden action is required" });
  if (event.action === "override" && !event.overrideGrantToken) context.addIssue({ code: "custom", path: ["overrideGrantToken"], message: "The supervisor grant token is required" });
  if (!event.credentialVersion) context.addIssue({ code: "custom", path: ["credentialVersion"], message: "A credential version is required" });
});

const guardCredentialSnapshotSchema = z.object({
  id: z.string().min(1),
  credentialType: z.enum(guardCredentialTypes),
  sourceType: z.string().min(1),
  sourceId: z.string().min(1),
  status: z.string().min(1),
  validFrom: z.number().int().positive(),
  validUntil: z.number().int().positive(),
  displayLabel: z.string().min(1),
  audience: z.string().optional(),
  entryMode: z.string().optional(),
  scope: z.record(z.string(), z.unknown()).optional(),
  version: z.number().int().positive(),
  sourceVersion: z.number().int().positive().optional(),
  unitLabel: z.string().optional(),
  vehicle: z.object({ make: z.string(), model: z.string(), color: z.string() }).optional(),
  plateHash: z.string().regex(/^[a-f0-9]{64}$/u).optional(),
  rfidHash: z.string().regex(/^[a-f0-9]{64}$/u).optional(),
}).superRefine((credential, context) => {
  if (credential.credentialType !== "vehicle" && !credential.sourceVersion) {
    context.addIssue({ code: "custom", path: ["sourceVersion"], message: "Projected credentials require a source version" });
  }
});

export const guardBundlePayloadSchema = z.object({
  schemaVersion: z.literal(1),
  bundleId: z.uuid(),
  tenantId: z.string().min(1),
  deviceId: z.string().min(1),
  sessionId: z.string().min(1),
  gateScope: z.string().min(1),
  policyVersion: z.number().int().positive(),
  issuedAt: z.number().int().positive(),
  expiresAt: z.number().int().positive(),
  serverTime: z.number().int().positive(),
  keyId: z.string().min(1).max(200),
  acceptedKeyIds: z.array(z.string().min(1).max(200)).min(1).max(20),
  maxClockSkewSeconds: z.number().int().nonnegative().max(120),
  allowedActions: z.array(z.enum(guardActions)),
  requireLocalIdCheck: z.boolean(),
  credentials: z.array(guardCredentialSnapshotSchema),
  vehicles: z.array(guardCredentialSnapshotSchema),
}).superRefine((bundle, context) => {
  if (bundle.expiresAt <= bundle.issuedAt) context.addIssue({ code: "custom", path: ["expiresAt"], message: "Bundle expiry must follow issuance" });
  if (bundle.vehicles.some((vehicle) => vehicle.credentialType !== "vehicle" || vehicle.sourceType !== "vehicle")) context.addIssue({ code: "custom", path: ["vehicles"], message: "Vehicle snapshots must use vehicle source metadata" });
  if (bundle.credentials.some((credential) => credential.credentialType === "vehicle")) context.addIssue({ code: "custom", path: ["credentials"], message: "Vehicle snapshots must not be credentials" });
});

export const guardEventSyncSchema = z.object({
  deviceId: z.string().min(1).max(100),
  sessionId: z.string().min(1).max(100),
  events: z.array(guardEventSchema).min(1).max(100),
});

export const guardOverrideGrantSchema = z.object({
  deviceId: z.string().min(1).max(100),
  sessionId: z.string().min(1).max(100),
  reason: z.string().trim().min(3).max(500),
  expiresAt: z.number().int().positive().optional(),
});

export const guardExceptionResolutionSchema = z.object({
  decision: z.enum(["accept", "reject", "retry"]),
  note: z.string().trim().min(3).max(1_000),
});

export const guardRetentionHoldSchema = z.object({
  held: z.boolean(),
  reason: z.string().trim().min(3).max(500),
});

export const guardIntakeSourceTypes = ["visitor", "pool"] as const;
export const guardIntakeSessionSchema = z.object({
  gateScope: z.string().trim().min(1).max(120),
  sourceTypes: z.array(z.enum(guardIntakeSourceTypes)).min(1).max(2).default(["visitor", "pool"]),
}).superRefine((session, context) => {
  if (new Set(session.sourceTypes).size !== session.sourceTypes.length) {
    context.addIssue({
      code: "custom",
      path: ["sourceTypes"],
      message: "Source types must be unique",
    });
  }
});

const guardIntakeBaseSchema = z.object({
  unitCode: z.string().trim().min(1).max(30),
  unitId: z.string().trim().min(1).max(100).optional(),
  hostName: z.string().trim().min(2).max(120).optional(),
  contactPhone: z.string().trim().max(60).optional(),
});

export const guardIntakeSubmitSchema = z.discriminatedUnion("sourceType", [
  guardIntakeBaseSchema.extend({
    sourceType: z.literal("visitor"),
    guestName: z.string().trim().min(2).max(120),
    purpose: z.enum(visitorPurposes),
    partySize: z.number().int().min(1).max(20),
    visitStartsAt: z.number().int().positive(),
    visitEndsAt: z.number().int().positive(),
    vehiclePlate: z.string().trim().max(20).optional(),
    notes: z.string().trim().max(500).optional(),
  }).superRefine((visit, context) => {
    if (visit.visitEndsAt <= visit.visitStartsAt) context.addIssue({ code: "custom", path: ["visitEndsAt"], message: "Visit must end after it starts" });
    if (visit.visitEndsAt - visit.visitStartsAt > 86_400) context.addIssue({ code: "custom", path: ["visitEndsAt"], message: "A visitor pass cannot exceed 24 hours" });
  }),
  guardIntakeBaseSchema.extend({
    sourceType: z.literal("pool"),
    attendeeName: z.string().trim().min(2).max(120),
    visitDate: z.string().date(),
    partySize: z.number().int().min(1).max(10),
    digitalSignatureName: z.string().trim().min(2).max(120),
    acceptedRules: z.literal(true),
  }).superRefine((pass, context) => {
    if (pass.attendeeName.toLocaleLowerCase() !== pass.digitalSignatureName.toLocaleLowerCase()) context.addIssue({ code: "custom", path: ["digitalSignatureName"], message: "Digital signature must match the attendee name" });
  }),
]);

export const guardIntakeDecisionSchema = z.object({
  decision: z.enum(["issue", "hold", "deny"]),
  reason: z.string().trim().max(500).optional(),
  localIdChecked: z.boolean().default(false),
}).superRefine((decision, context) => {
  if (["hold", "deny"].includes(decision.decision) && (!decision.reason || decision.reason.length < 3)) context.addIssue({ code: "custom", path: ["reason"], message: "A reason is required" });
});

export const packageClaimSchema = z.object({ publicNumber: z.string().trim().min(1).max(80).optional(), claimCode: z.string().regex(/^\d{6}$/u) });
export const packageLinkSchema = z.object({ userId: z.string().min(1).max(100) });

export const featureKeys = ["billing", "community", "marketplace", "events", "visitors", "permits", "packages", "mobility", "staff", "access_wallet", "pmo_transparency", "maintenance_concierge", "tax_compliance"] as const;
export const featureUpdateSchema = z.object({ featureKey: z.enum(featureKeys), enabled: z.boolean() });
export const approvedVendorSchema = z.object({
  legalName: z.string().trim().min(2).max(160), serviceCategory: z.string().trim().min(2).max(80),
  contactName: z.string().trim().max(120).optional(), contactEmail: z.email().optional(), contactPhone: z.string().trim().max(60).optional(),
  websiteUrl: z.url().optional(), reviewNotes: z.string().trim().min(5).max(1_000), reviewExpiresAt: z.number().int().positive().optional(),
});
export const vendorReviewSchema = z.object({ status: z.enum(["reviewed", "suspended", "expired"]), note: z.string().trim().min(3).max(1_000) });
export const journalEntrySchema = z.object({
  entryDate: z.string().date(), memo: z.string().trim().min(3).max(500),
  lines: z.array(z.object({ accountId: z.string().min(1).max(100), description: z.string().trim().max(300).optional(), debitCentavos: z.number().int().min(0).max(1_000_000_000).default(0), creditCentavos: z.number().int().min(0).max(1_000_000_000).default(0), unitId: z.string().min(1).max(100).optional() }).superRefine((line, context) => { if ((line.debitCentavos > 0) === (line.creditCentavos > 0)) context.addIssue({ code: "custom", message: "Each line must contain one debit or credit" }); })).min(2).max(100),
}).superRefine((entry, context) => { const debit = entry.lines.reduce((sum, line) => sum + line.debitCentavos, 0); const credit = entry.lines.reduce((sum, line) => sum + line.creditCentavos, 0); if (debit !== credit || debit === 0) context.addIssue({ code: "custom", path: ["lines"], message: "Journal must balance" }); const debitAccounts = new Set(entry.lines.filter((line) => line.debitCentavos > 0).map((line) => line.accountId)); const creditAccounts = new Set(entry.lines.filter((line) => line.creditCentavos > 0).map((line) => line.accountId)); for (const accountId of debitAccounts) if (creditAccounts.has(accountId)) context.addIssue({ code: "custom", path: ["lines"], message: "An account cannot be both debited and credited in the same journal" }); });
export const reconciliationSchema = z.object({ note: z.string().trim().max(500).optional() });
export const chartAccountSchema = z.object({ code: z.string().trim().min(2).max(20), name: z.string().trim().min(3).max(160), accountType: z.enum(["asset", "liability", "equity", "revenue", "expense"]), normalBalance: z.enum(["debit", "credit"]), systemKey: z.string().trim().min(2).max(80).optional() });
export const accountingPeriodSchema = z.object({ label: z.string().trim().min(3).max(80), startsOn: z.string().date(), endsOn: z.string().date() }).superRefine((period, context) => { if (period.endsOn < period.startsOn) context.addIssue({ code: "custom", path: ["endsOn"], message: "End date must follow start date" }); });
export const utilityMeterSchema = z.object({ unitId: z.string().min(1).max(100), utilityType: z.enum(["electricity", "water"]), meterNumber: z.string().trim().min(2).max(80), provider: z.string().trim().min(2).max(80).default("ACEN"), installedAt: z.string().date().optional() });
export const utilityRateSchema = z.object({ utilityType: z.enum(["electricity", "water"]), name: z.string().trim().min(3).max(120), unitOfMeasure: z.enum(["kWh", "m3"]), ratePerUnitCentavos: z.number().int().min(0).max(10_000_000), effectiveFrom: z.string().date(), sourceReference: z.string().trim().min(3).max(500) });
export const meterReadingSchema = z.object({ readingDate: z.string().date(), readingMilliunits: z.number().int().min(0).max(1_000_000_000_000), externalDocumentNumber: z.string().trim().max(100).optional(), sourceReference: z.string().trim().max(500).optional() });
export const assessmentRuleSchema = z.object({ code: z.string().trim().min(2).max(40), name: z.string().trim().min(3).max(160), category: z.enum(["association_dues", "parking_fee"]), unitId: z.string().min(1).max(100), parkingSlotId: z.string().min(1).max(100).optional(), monthlyAmountCentavos: z.number().int().min(0).max(100_000_000), effectiveFrom: z.string().date(), effectiveTo: z.string().date().optional() }).superRefine((rule, context) => { if (rule.category === "parking_fee" && !rule.parkingSlotId) context.addIssue({ code: "custom", path: ["parkingSlotId"], message: "Parking slot is required" }); if (rule.category === "association_dues" && rule.parkingSlotId) context.addIssue({ code: "custom", path: ["parkingSlotId"], message: "Association dues cannot reference a parking slot" }); if (rule.effectiveTo && rule.effectiveTo < rule.effectiveFrom) context.addIssue({ code: "custom", path: ["effectiveTo"], message: "End date must follow start date" }); });
export const assessmentBatchSchema = z.object({ year: z.number().int().min(2020).max(2100), quarter: z.number().int().min(1).max(4) });
export const paymentRecordSchema = z.object({ receivedDate: z.string().date(), amountCentavos: z.number().int().positive().max(1_000_000_000), payerName: z.string().trim().min(2).max(160), paymentMethod: z.enum(["bank_transfer", "bills_payment", "e_wallet", "check", "other"]), externalReference: z.string().trim().min(3).max(160), customerReferenceNumber: z.string().trim().max(80).optional(), proofDocumentId: z.string().min(1).max(100).optional() });
export const paymentMatchSchema = z.object({ unitId: z.string().min(1).max(100), allocations: z.array(z.object({ billingItemId: z.string().min(1).max(100), amountCentavos: z.number().int().positive() })).max(100).optional() });
export const paymentReconciliationSchema = z.object({ periodStart: z.string().date(), periodEnd: z.string().date(), note: z.string().trim().max(500).optional() }).superRefine((reconciliation, context) => { if (reconciliation.periodEnd < reconciliation.periodStart) context.addIssue({ code: "custom", path: ["periodEnd"], message: "End date must follow start date" }); });
export const createPoolPassSchema = z.object({ unitId: z.string().min(1).max(100), visitDate: z.string().date(), attendeeName: z.string().trim().min(2).max(120), partySize: z.number().int().min(1).max(10), digitalSignatureName: z.string().trim().min(2).max(120), acceptedRules: z.literal(true) }).superRefine((pass, context) => { if (pass.attendeeName.toLocaleLowerCase() !== pass.digitalSignatureName.toLocaleLowerCase()) context.addIssue({ code: "custom", path: ["digitalSignatureName"], message: "Digital signature must match the attendee name" }); });
export const poolPassTokenSchema = z.object({ token: z.string().min(40).max(64) });
export const poolPassTransitionSchema = poolPassTokenSchema.extend({ action: z.enum(["check_in", "check_out", "deny"]), note: z.string().trim().max(500).optional() }).superRefine((transition, context) => { if (transition.action === "deny" && (!transition.note || transition.note.length < 3)) context.addIssue({ code: "custom", path: ["note"], message: "A denial reason is required" }); });

export const facilityReservationSettingsSchema = z.object({
  enabled: z.boolean(), approvalMode: z.enum(["required", "automatic"]), operatingDays: z.string().regex(/^[1-7](,[1-7])*$/), opensAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/), closesAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/), slotMinutes: z.number().int().min(15).max(720), bufferMinutes: z.number().int().min(0).max(240), capacity: z.number().int().min(1).max(500), feeCentavos: z.number().int().min(0).nullable().optional(), requireCheckIn: z.boolean(), poolPassRequired: z.boolean(),
}).superRefine((settings, context) => { if (settings.closesAt <= settings.opensAt) context.addIssue({ code: "custom", path: ["closesAt"], message: "Closing time must be after opening time" }); });
export const facilityReservationSchema = z.object({ facilityId: z.string().min(1), unitId: z.string().min(1).optional(), buildingId: z.string().min(1).optional(), unitCode: z.string().trim().max(40).optional(), reservationDate: z.string().date(), startsAt: z.number().int().positive(), endsAt: z.number().int().positive(), partySize: z.number().int().min(1).max(500), purpose: z.string().trim().min(3).max(500), notes: z.string().trim().max(1_000).optional() }).refine((value) => value.endsAt > value.startsAt, { path: ["endsAt"], message: "End time must be after start time" });
export const externalFacilityReservationSchema = facilityReservationSchema.extend({ requesterName: z.string().trim().min(2).max(120), requesterEmail: z.email(), turnstileToken: z.string().min(1) });
export const facilityReservationDecisionSchema = z.object({ decision: z.enum(["approved", "rejected", "cancelled", "completed", "no_show", "cancellation_denied"]), reason: z.string().trim().min(5).max(1_000) });
export const facilityReservationCancellationSchema = z.object({ reason: z.string().trim().min(5).max(500) });
export const facilityReservationTokenSchema = z.object({ token: z.string().min(20).max(128) });

export const invitationAcceptSchema = z.object({
  token: z.string().min(40).max(64),
  turnstileToken: z.string().min(1),
  name: z.string().trim().min(2).max(120),
  password: z.string().min(12).max(128),
});

export const registrationDecisionSchema = z.object({
  decision: z.enum(["approved", "rejected"]),
  reason: z.string().trim().max(1_000).optional(),
});

const ticketTransitions: Record<TicketStatus, readonly TicketStatus[]> = {
  open: ["triaged", "cancelled"],
  triaged: ["scheduled", "in_progress", "cancelled"],
  scheduled: ["in_progress", "awaiting_parts", "awaiting_resident", "awaiting_vendor", "cancelled"],
  in_progress: ["scheduled", "awaiting_parts", "awaiting_resident", "awaiting_vendor", "resolved", "cancelled"],
  awaiting_parts: ["in_progress", "scheduled", "resolved", "cancelled"],
  awaiting_resident: ["in_progress", "scheduled", "resolved", "cancelled"],
  awaiting_vendor: ["in_progress", "scheduled", "resolved", "cancelled"],
  resolved: ["reopened", "closed"],
  reopened: ["triaged", "scheduled", "in_progress"],
  closed: [],
  cancelled: [],
};

export function canTransitionTicket(from: TicketStatus, to: TicketStatus) {
  return ticketTransitions[from].includes(to);
}

export function expectedRoleFamily(role: Role): RoleFamily {
  return role.startsWith("resident_") ? "resident" : "employee";
}

export function residentRoleForRelationships(relationships: readonly string[]): Role {
  if (relationships.some((relationship) => relationship === "owner" || relationship === "co_owner")) return "resident_owner";
  if (relationships.includes("tenant")) return "resident_tenant";
  return "resident_occupant";
}

export function formatPeso(centavos: number) {
  return new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(centavos / 100);
}
